/**
 * Themes for the Visualization Playground.
 *
 * Two variants ship in Phase 1:
 *
 *   • "dark" / constellation — the Hooktheory-derived language: a near-black
 *     field, white opacity-weighted edges, white glow halos. Every value is
 *     sourced verbatim from the visualization research corpus.
 *
 *   • "editorial" — a light, print-inspired variant (warm off-white field,
 *     deep ink edges, refined palette, colored halos + hairline borders). Uses
 *     the editorial design tokens (Playfair / Source Sans / JetBrains Mono,
 *     navy accent #1E3A6E).
 *
 * The renderer reads a Theme; lenses change look by swapping the theme, not by
 * touching draw code.
 */

export type ThemeName = "dark" | "editorial";

export interface Theme {
  name: ThemeName;
  background: string;
  /** Only the top N clusters get a palette color; the rest go neutral. */
  maxColoredClusters: number;
  palette: readonly string[];
  neutralCluster: string;

  edge: {
    stroke: string;
    /** Constellation band — narrow (≈4:1 ratio) to preserve the gestalt. */
    opacityMin: number;
    opacityMax: number;
    widthMin: number;
    widthMax: number;
    colorByType: Record<string, string>;
  };

  node: {
    opacity: number;
    /** node.confidence maps into this band. */
    opacityMin: number;
    opacityMax: number;
    /** Sub-linear radius band. */
    radiusMin: number;
    radiusMax: number;
    /** Optional hairline border on every node (editorial). */
    stroke?: string;
    strokeWidth?: number;
  };

  glow: {
    /** "white" = white outer blur (dark theme); "cluster" = colored halo (editorial). */
    outerColor: "white" | "cluster";
    outerScale: number;
    middleScale: number;
    /** Opacity of the outer halo fill. */
    haloOpacity: number;
    blurByTier: Record<number, number>;
    /** Optional crisp accent ring around status nodes (editorial). */
    ring?: { color: string; widthByTier: Record<number, number> };
  };

  pattern: { size: number; strokeColor: string };

  label: {
    fontFamily: string;
    fontSize: number;
    color: string;
    valueColor: string;
    valueFontFamily?: string;
  };
}

const SANS =
  '"Source Sans 3", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace';

export const DARK_THEME: Theme = {
  name: "dark",
  background: "#0a0a0a",
  maxColoredClusters: 7,
  palette: [
    "#A62114", // tonic
    "#4CB014", // subdominant
    "#3094A7", // dominant
    "#7E00B0", // relative minor
    "#A65F16", // ii
    "#A89917", // iii
    "#A7009C", // other
  ],
  neutralCluster: "#5a5a5a",
  edge: {
    stroke: "#ffffff",
    opacityMin: 0.04,
    opacityMax: 0.15,
    widthMin: 0.5,
    widthMax: 4,
    colorByType: {
      semantic: "#ffffff",
      reference: "#3094A7",
      causal: "#A65F16",
    },
  },
  node: {
    opacity: 0.92,
    opacityMin: 0.4,
    opacityMax: 0.95,
    radiusMin: 6,
    radiusMax: 32,
  },
  glow: {
    outerColor: "white",
    outerScale: 1.25,
    middleScale: 1.2,
    haloOpacity: 0.15,
    blurByTier: { 1: 2, 2: 3, 3: 4 },
  },
  pattern: { size: 4, strokeColor: "rgba(255,255,255,0.3)" },
  label: {
    fontFamily: SANS,
    fontSize: 9,
    color: "rgba(255,255,255,0.85)",
    valueColor: "rgba(255,255,255,0.5)",
    valueFontFamily: MONO,
  },
};

export const EDITORIAL_THEME: Theme = {
  name: "editorial",
  background: "#FAF8F5",
  maxColoredClusters: 7,
  // Deeper, print-inspired palette that harmonizes with the navy accent on warm white.
  palette: [
    "#1E3A6E", // navy (accent)
    "#A6321E", // brick
    "#2E7D5B", // forest
    "#8A6D1F", // ochre
    "#6B3FA0", // violet
    "#2C7B8C", // teal
    "#A14A7B", // mauve
  ],
  neutralCluster: "#9a948c",
  edge: {
    stroke: "#1A1A1A",
    // Dark-on-light needs a touch more ink than white-on-dark to read the same.
    opacityMin: 0.05,
    opacityMax: 0.2,
    widthMin: 0.5,
    widthMax: 4,
    colorByType: {
      semantic: "#1A1A1A",
      reference: "#2C7B8C",
      causal: "#A6321E",
    },
  },
  node: {
    opacity: 0.95,
    opacityMin: 0.45,
    opacityMax: 0.98,
    radiusMin: 6,
    radiusMax: 32,
    stroke: "rgba(26,26,26,0.18)",
    strokeWidth: 1,
  },
  glow: {
    outerColor: "cluster",
    outerScale: 1.35,
    middleScale: 1.18,
    haloOpacity: 0.28,
    blurByTier: { 1: 2.5, 2: 3.5, 3: 5 },
    ring: { color: "rgba(30,58,110,0.9)", widthByTier: { 1: 1, 2: 1.5, 3: 2.5 } },
  },
  pattern: { size: 4, strokeColor: "rgba(255,255,255,0.55)" },
  label: {
    fontFamily: SANS,
    fontSize: 9.5,
    color: "rgba(26,26,26,0.9)",
    valueColor: "rgba(26,26,26,0.45)",
    valueFontFamily: MONO,
  },
};

export const THEMES: Record<ThemeName, Theme> = {
  dark: DARK_THEME,
  editorial: EDITORIAL_THEME,
};

/**
 * Default force parameters. Hooktheory's literal starter was charge -30 on a
 * 288px canvas; on a full viewport that collapses to a dot, so these are tuned
 * to fill the frame (fit-to-view handles final framing).
 */
export const DEFAULT_FORCES = {
  linkStrength: 0.4,
  linkDistance: 70,
  charge: -240,
  centerGravity: 0.06,
  clusterGravity: 0.04,
} as const;
